/**
 * AgentProfile (CP3, Screen #6) — per-rep development page.
 *
 * Manager/admin only; a DRILL-IN under Coaching (reached by clicking a rep on
 * the manager dashboard / coaching inbox — intentionally NOT a Sidebar item).
 * Renders GET /api/manager/agents/:id:
 *   - header: rep name, extension, role, active state;
 *   - summary: counted SALES scored calls + average (dashboard semantics);
 *   - score trend: a dependency-free inline bar strip (oldest → newest);
 *   - per-call-type breakdown (service in its own bucket — never blended);
 *   - calibration gap (this rep's self-vs-AI row, when there's enough data);
 *   - open coaching items with lifecycle chips + legal-transition controls;
 *   - recent calls via GET /api/calls?extension=… (server-side scoping,
 *     reveal gate, and privacy blocklist all re-applied per request).
 *
 * The date window (from/to on the call's started_at) matches Calibration's
 * semantics, so numbers here line up with that screen for the same range.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type {
  AgentProfile as AgentProfileModel,
  AgentProfileFlag,
  CalibrationFilters,
  FlagLifecycleStatus,
} from "@/types";
import { FlagStatusBadge, FlagTransitionSelect } from "@/components/FlagLifecycle";
import { PageHeader, Card, Badge, Button } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { IconChevronLeft, IconInfo } from "@/components/icons";
import { scoreBand, scoreBandClasses, secToClock } from "@/lib/format";

// ── Date window (mirrors Calibration's presets / server filter mapping) ──────
type DatePreset = "" | "today" | "7d" | "30d" | "month";
function presetRange(preset: DatePreset): { from: string; to: string } {
  if (!preset) return { from: "", to: "" };
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const now = new Date();
  const today = fmt(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "month")
    return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
  const back = preset === "7d" ? 6 : 29;
  const start = new Date(now);
  start.setDate(start.getDate() - back);
  return { from: fmt(start), to: today };
}

interface DateState {
  from: string; // yyyy-mm-dd local
  to: string;
}

function toServerFilters(d: DateState): CalibrationFilters {
  return {
    ...(d.from ? { from: new Date(`${d.from}T00:00:00`).toISOString() } : {}),
    ...(d.to ? { to: new Date(`${d.to}T23:59:59.999`).toISOString() } : {}),
  };
}

// Friendly labels for the call types (mirrors the admin grader tab).
const CALL_TYPE_LABELS: Record<string, string> = {
  automotive_tint: "Automotive tint",
  ppf: "Paint protection film (PPF)",
  arch_residential: "Architectural — residential",
  arch_commercial: "Architectural — commercial",
  non_sales_admin: "Non-sales / admin",
  unknown: "Unknown",
};
function callTypeLabel(ct: string): string {
  return CALL_TYPE_LABELS[ct] ?? ct;
}

/** Bare text color for a 0–100 score (matches the band tokens). */
function scoreTextClass(total: number): string {
  switch (scoreBand(total)) {
    case "strong":
      return "text-band-strong";
    case "solid":
      return "text-band-solid";
    case "fair":
      return "text-band-fair";
    case "weak":
      return "text-band-weak";
  }
}

/** Per-bar fill class by score band. */
function barClass(total: number): string {
  switch (scoreBand(total)) {
    case "strong":
      return "bg-band-strong";
    case "solid":
      return "bg-band-solid";
    case "fair":
      return "bg-band-fair";
    case "weak":
      return "bg-band-weak";
  }
}

/** Signed one-decimal gap ("+4.0" / "-3.0" / "0.0") — calibration display. */
function fmtGap(gap: number): string {
  const rounded = Math.round(gap * 10) / 10;
  if (rounded > 0) return `+${rounded.toFixed(1)}`;
  return rounded.toFixed(1);
}

const TREND_DISPLAY_MAX = 60; // bars shown (server caps the series at 200)

/**
 * Dependency-free score trend: one band-colored vertical bar per counted call,
 * oldest → newest, height ∝ total/100. Pure divs + CSS (no chart lib).
 */
function TrendStrip({ trend }: { trend: AgentProfileModel["trend"] }) {
  const shown = trend.slice(-TREND_DISPLAY_MAX);
  if (shown.length === 0) {
    return <p className="text-sm text-muted-foreground">No scored sales calls in this window yet.</p>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="flex h-20 items-end gap-[3px]"
        role="img"
        aria-label={`Scores, oldest to newest: ${shown.map((p) => p.total).join(", ")}`}
      >
        {shown.map((p) => {
          const clamped = Math.max(0, Math.min(100, p.total));
          return (
            <Link
              key={p.call_id}
              to={`/calls/${p.call_id}`}
              title={`${p.total}/100 · ${
                p.started_at ? new Date(p.started_at).toLocaleDateString() : "—"
              }`}
              className={`w-2 rounded-sm opacity-80 transition hover:opacity-100 ${barClass(p.total)}`}
              // min 8% so a zero/low score still shows a sliver.
              style={{ height: `${Math.max(8, clamped)}%` }}
            />
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {trend.length > shown.length
          ? `Last ${shown.length} of ${trend.length} scored sales calls (oldest → newest). `
          : `${shown.length} scored sales call${shown.length === 1 ? "" : "s"} (oldest → newest). `}
        Click a bar to open the call.
      </p>
    </div>
  );
}

/** Open coaching items table (lifecycle chips + legal-transition controls). */
function OpenItems({
  repId,
  flags,
}: {
  repId: string;
  flags: AgentProfileFlag[];
}) {
  const queryClient = useQueryClient();
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const transition = useMutation({
    // flagId pins the exact row server-side — a call can carry both a manual
    // and an auto_review flag, and this row's id is authoritative.
    mutationFn: (vars: { flagId: string; callId: string; status: FlagLifecycleStatus }) =>
      api.transitionFlag(vars.callId, vars.status, vars.flagId),
    onMutate: () => setTransitionError(null),
    onError: (err) => {
      setTransitionError(
        err instanceof ApiRequestError && err.body?.message
          ? err.body.message
          : "Couldn't update the coaching item. Please try again.",
      );
    },
    onSuccess: (_data, vars) => {
      // Partial key: refresh this rep's profile for EVERY date window.
      void queryClient.invalidateQueries({ queryKey: ["manager", "agent", repId] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.manager.queue });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.coaching(vars.callId),
      });
    },
  });

  if (flags.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No open coaching items for this rep.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Note</th>
                <th className="px-4 py-2 font-medium">Due</th>
                <th className="px-4 py-2 font-medium">Call</th>
                <th className="px-4 py-2 font-medium">Move</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((f) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-accent/40">
                  <td className="px-4 py-2">
                    <Badge
                      tone={
                        f.type === "exemplary"
                          ? "success"
                          : f.type === "auto_review"
                            ? "danger"
                            : "warning"
                      }
                    >
                      {f.type === "exemplary"
                        ? "Exemplary"
                        : f.type === "auto_review"
                          ? "Auto review"
                          : "Needs work"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <FlagStatusBadge status={f.status} />
                      {f.acknowledged_at && (
                        <span
                          className="text-xs text-muted-foreground"
                          title={`Acknowledged ${new Date(f.acknowledged_at).toLocaleString()}`}
                        >
                          ✓ seen
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 max-w-xs">
                    {f.note ? (
                      <span className="line-clamp-2 text-muted-foreground">{f.note}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {f.due_date ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      className="hover:underline"
                      to={`/calls/${f.call_id}`}
                      title="Open the call"
                    >
                      {f.started_at
                        ? new Date(f.started_at).toLocaleString()
                        : "View call"}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <FlagTransitionSelect
                      status={f.status}
                      pending={
                        transition.isPending &&
                        transition.variables?.flagId === f.id
                      }
                      onTransition={(to) =>
                        transition.mutate({ flagId: f.id, callId: f.call_id, status: to })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {transitionError && <p className="text-sm text-destructive">{transitionError}</p>}
    </div>
  );
}

const RECENT_CALLS_LIMIT = 10;

/**
 * Recent calls on the rep's extension, via the normal list endpoint — the
 * server re-applies role scoping, the reveal gate, and the privacy blocklist
 * on every request, so this table can never show a manager a blocked call.
 */
function RecentCalls({ extension }: { extension: string | null }) {
  const filters = useMemo(
    () => ({ extension: extension ?? undefined, limit: RECENT_CALLS_LIMIT, offset: 0 }),
    [extension],
  );
  const callsQuery = useQuery({
    queryKey: queryKeys.calls.list(filters),
    queryFn: ({ signal }) => api.listCallsPaged(filters, signal),
    enabled: extension !== null,
  });

  if (extension === null) {
    return (
      <p className="text-sm text-muted-foreground">
        No extension is mapped to this rep, so their recent calls can't be listed here.
      </p>
    );
  }
  if (callsQuery.isLoading) return <LoadingState label="Loading recent calls…" />;
  if (callsQuery.isError) {
    return (
      <ErrorState error={callsQuery.error} onRetry={() => void callsQuery.refetch()} />
    );
  }
  const rows = callsQuery.data?.rows ?? [];
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No calls on ext {extension} yet.</p>;
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Date / Time</th>
              <th className="px-4 py-2 font-medium">Direction</th>
              <th className="px-4 py-2 font-medium">Duration</th>
              <th className="px-4 py-2 text-center font-medium">Self</th>
              <th className="px-4 py-2 text-center font-medium">AI</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-accent/40">
                <td className="px-4 py-2">
                  <Link className="font-medium hover:underline" to={`/calls/${c.id}`}>
                    {c.started_at ? new Date(c.started_at).toLocaleString() : "—"}
                  </Link>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.direction}</td>
                <td className="px-4 py-2 tabular-nums text-muted-foreground">
                  {secToClock(c.duration_sec)}
                </td>
                <td className="px-4 py-2 text-center">
                  {c.self_score === null ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreBandClasses(c.self_score)}`}
                    >
                      {c.self_score}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {c.ai_review_score === null ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${scoreBandClasses(c.ai_review_score)}`}
                    >
                      {c.ai_review_score}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function AgentProfile() {
  const { id } = useParams<{ id: string }>();
  const [datePreset, setDatePreset] = useState<DatePreset>("");
  const [dates, setDates] = useState<DateState>({ from: "", to: "" });
  const filters = useMemo(() => toServerFilters(dates), [dates]);

  const profileQuery = useQuery({
    queryKey: queryKeys.manager.agent(id ?? "", filters),
    queryFn: ({ signal }) => api.getAgentProfile(id ?? "", filters, signal),
    enabled: !!id,
  });

  function onPreset(p: DatePreset) {
    setDatePreset(p);
    setDates(presetRange(p));
  }

  const data = profileQuery.data;
  const notFound =
    profileQuery.error instanceof ApiRequestError && profileQuery.error.status === 404;

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title={data ? data.rep.name ?? "Agent profile" : "Agent profile"}
        subtitle={
          data
            ? [
                data.rep.extension_number ? `Ext ${data.rep.extension_number}` : null,
                data.rep.role,
                "development profile",
              ]
                .filter(Boolean)
                .join(" · ")
            : "Per-rep development: trend, call-type breakdown, calibration, and open coaching."
        }
        actions={
          <Link
            to="/manager"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <IconChevronLeft className="h-4 w-4" /> Coaching
          </Link>
        }
      />

      <div className="flex flex-1 flex-col gap-6 p-5">
        {profileQuery.isLoading && <LoadingState label="Loading agent profile…" />}
        {notFound && (
          <EmptyState
            title="Rep not found"
            description="This rep doesn't exist in your shop (the link may be stale)."
            action={
              <Link to="/manager">
                <Button variant="ghost">Back to Coaching</Button>
              </Link>
            }
          />
        )}
        {profileQuery.isError && !notFound && (
          <ErrorState error={profileQuery.error} onRetry={() => void profileQuery.refetch()} />
        )}

        {data && (
          <>
            {/* Date window (calibration semantics — same numbers as that screen). */}
            <Card className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">Quick range</span>
                <select
                  value={datePreset}
                  onChange={(e) => onPreset(e.target.value as DatePreset)}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">All time</option>
                  <option value="today">Today</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="month">This month</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">From</span>
                <input
                  type="date"
                  value={dates.from}
                  max={dates.to || undefined}
                  onChange={(e) => {
                    setDatePreset("");
                    setDates({ ...dates, from: e.target.value });
                  }}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">To</span>
                <input
                  type="date"
                  value={dates.to}
                  min={dates.from || undefined}
                  onChange={(e) => {
                    setDatePreset("");
                    setDates({ ...dates, to: e.target.value });
                  }}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              {(dates.from || dates.to) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDatePreset("");
                    setDates({ from: "", to: "" });
                  }}
                >
                  Clear
                </Button>
              )}
              {!data.rep.active && (
                <span className="ml-auto">
                  <Badge tone="muted">Deactivated</Badge>
                </span>
              )}
            </Card>

            {/* Summary row */}
            <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Avg. sales score
                </p>
                <p
                  className={`mt-1 text-3xl font-semibold tabular-nums ${
                    data.summary.avg_total === null
                      ? "text-muted-foreground"
                      : scoreTextClass(data.summary.avg_total)
                  }`}
                >
                  {data.summary.avg_total === null ? "—" : data.summary.avg_total}
                  {data.summary.avg_total !== null && (
                    <span className="ml-1 text-base font-normal text-muted-foreground">
                      /100
                    </span>
                  )}
                </p>
              </Card>
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Scored sales calls
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums">
                  {data.summary.scored_count}
                </p>
              </Card>
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Calibration gap
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums">
                  {data.calibration ? fmtGap(data.calibration.avg_gap) : "—"}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {data.calibration
                    ? data.calibration.over_scorer
                      ? "Self-scores higher than the AI"
                      : data.calibration.under_scorer
                        ? "Self-scores lower than the AI"
                        : "Well calibrated"
                    : "Needs submitted self-scores + AI scores"}
                </p>
              </Card>
              <Card>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Open coaching items
                </p>
                <p className="mt-1 text-3xl font-semibold tabular-nums">
                  {data.open_flags.length}
                </p>
              </Card>
            </section>

            {/* Score trend */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Score trend</h2>
              <Card>
                <TrendStrip trend={data.trend} />
              </Card>
            </section>

            {/* Per-call-type breakdown */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">By call type</h2>
              {Object.keys(data.per_type).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No scored calls in this window yet.
                </p>
              ) : (
                <Card className="overflow-hidden p-0">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 font-medium">Call type</th>
                        <th className="px-4 py-2 text-right font-medium">Calls</th>
                        <th className="px-4 py-2 text-right font-medium">Avg score</th>
                        <th className="w-1/3 px-4 py-2 font-medium" aria-hidden />
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(data.per_type)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([ct, agg]) => (
                          <tr key={ct} className="border-b last:border-0">
                            <td className="px-4 py-2">
                              {callTypeLabel(ct)}
                              {ct === "non_sales_admin" && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (tracked separately)
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                              {agg.count}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {agg.avg === null ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <span className={`font-semibold tabular-nums ${scoreTextClass(agg.avg)}`}>
                                  {agg.avg}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              {agg.avg !== null && (
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={`h-full rounded-full ${barClass(agg.avg)}`}
                                    style={{ width: `${Math.max(2, Math.min(100, agg.avg))}%` }}
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </section>

            {/* Calibration detail */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Calibration (self vs AI)</h2>
              {data.calibration ? (
                <Card className="flex flex-wrap items-center gap-x-8 gap-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Calls compared</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {data.calibration.n_calls}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg self</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {data.calibration.avg_self}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Avg AI</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {data.calibration.avg_ai}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Gap (self − AI)</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {fmtGap(data.calibration.avg_gap)}
                    </p>
                  </div>
                  {data.calibration.avg_override !== null && (
                    <div>
                      <p className="text-xs text-muted-foreground">Avg mgr score</p>
                      <p className="text-lg font-semibold tabular-nums">
                        {data.calibration.avg_override}
                      </p>
                    </div>
                  )}
                  <div className="ml-auto">
                    {data.calibration.over_scorer ? (
                      <Badge tone="warning">Over-scores</Badge>
                    ) : data.calibration.under_scorer ? (
                      <Badge tone="primary">Under-scores</Badge>
                    ) : (
                      <Badge tone="success">Calibrated</Badge>
                    )}
                  </div>
                </Card>
              ) : (
                <Card className="flex items-start gap-2.5 bg-muted/20">
                  <IconInfo className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Not enough data: calibration needs calls in this window with both a
                    submitted self-score and an AI score.
                  </p>
                </Card>
              )}
            </section>

            {/* Open coaching items (lifecycle) */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Open coaching items</h2>
              <OpenItems repId={data.rep.id} flags={data.open_flags} />
            </section>

            {/* Recent calls (server-scoped + blocklist-respecting) */}
            <section className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold">Recent calls</h2>
              <RecentCalls extension={data.rep.extension_number} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}
